(ns manatee.admin.task-config.documents.handlers
  "Re-frame event handlers for the task documents views."
  (:require
   [ajax.edn :as edn]
   [kee-frame.core :as kf]
   [manatee.admin.task-config.documents.app-db :as db]
   [manatee.components.notifications.handlers :as notification-handlers]
   [re-frame.core :as rf]))

;; View
;; ====

(rf/reg-event-fx
 ::open-task-documents-view
 (fn open-documents-view
   [_cofx [_ task-config-id]]
   {:dispatch-n [[::clear-documents-view]
                 [:navigate-to [:manatee.task-config.documents.handlers/task-documents :task-config-id task-config-id]]
                 [::fetch-task-documents task-config-id]]}))

(rf/reg-event-db
 ::clear-documents-view
 (fn clear-documents-view
   [db _]
   (db/reset db)))

(rf/reg-event-fx
 ::fetch-task-documents
 (fn fetch-task-documents
   [_cofx [_ task-config-id]]
   {:http-xhrio {:method :get
                 :uri (kf/path-for [:api/task-documents :task-config-id task-config-id])
                 :response-format (edn/edn-response-format)
                 :on-success [::fetch-task-documents-success]
                 :on-failure [::fetch-task-documents-failure]}}))

(rf/reg-event-db
 ::fetch-task-documents-success
 (fn fetch-task-documents-success
   [db [_ documents]]
   (db/set-documents db documents)))

(rf/reg-event-fx
 ::fetch-task-documents-failure
 (fn fetch-task-documents-failure
   [_cofx [_ _]]
   {:dispatch [::notification-handlers/add-alert :main
               {:message "Unable to load task documents, please refresh the page to try again."
                :type :error
                :auto-dismiss? false}]}))

;; Document Upload

(rf/reg-event-db
 ::set-upload-document-name
 (fn set-upload-document-name
   [db [_ name]]
   (db/set-document-upload-name db name)))

(rf/reg-event-db
 ::set-upload-document-file
 (fn set-upload-document-file
   [db [_ file]]
   (db/set-document-upload-file db file)))

(rf/reg-event-fx
 ::upload-document
 (fn upload-document
   [{:keys [db] :as _cofx} [_ task-config-id]]
   (let [{:keys [name file]} (db/get-document-upload-payload db)]
     {:db (db/set-uploading? db true)
      :http-xhrio {:method :post
                   :uri (kf/path-for [:api/task-documents :task-config-id task-config-id])
                   :body (doto (js/FormData.)
                           (.set "file" file name))
                   :response-format (edn/edn-response-format)
                   :on-success [::upload-document-success]
                   :on-failure [::upload-document-failure]}})))

(rf/reg-event-fx
 ::document-upload-form-reset
 (fn document-upload-form-reset
   ;; Clear the document upload form. There appears no way to do this from
   ;; within Re-frame, so we do it 'the old-fashioned way'.
   [_cofx [_]]
   (-> js/document
       (.getElementById "doc-upload-form")
       (.reset))
   {}))

(rf/reg-event-fx
 ::upload-document-success
 (fn upload-document-success
   [{:keys [db] :as _cofx} [_ documents]]
   {:db (-> db
            (db/set-uploading? false)
            (db/clear-document-upload-payload)
            (db/set-documents documents))
    :dispatch-n [[::document-upload-form-reset]
                 [::notification-handlers/add-alert :main
                  {:message "Task document uploaded successfully"
                   :type :info}
                  :auto-dismiss? true]]}))

(rf/reg-event-fx
 ::upload-document-failure
 (fn upload-document-failure
   [{:keys [db] :as _cofx} [_ info]]
   {:db (db/set-uploading? db false)
    :dispatch [::notification-handlers/add-alert :main
               {:message (if-let [reason (get-in info [:response :reason])]
                           (str "Unable to upload task document: " reason)
                           "Error uploading task document")
                :type :error}
               :auto-dismiss? false]}))

;; Documentation Link Upload

(rf/reg-event-db
 ::set-link-upload-document-name
 (fn set-link-upload-document-name
   [db [_ name]]
   (db/set-documentation-link-upload-name db name)))

(rf/reg-event-db
 ::set-link-upload-document-link
 (fn set-link-upload-document-link
   [db [_ link]]
   (db/set-documentation-link-upload-link db link)))

(rf/reg-event-fx
 ::upload-documentation-link
 (fn upload-documentation-link
   [{:keys [db] :as _cofx} [_ task-config-id]]
   (let [payload (db/get-documentation-link-upload-payload db)]
     {:db (db/set-link-uploading? db true)
      :http-xhrio {:method :post
                   :uri (kf/path-for [:api/task-documentation-links :task-config-id task-config-id])
                   :params payload
                   :format (edn/edn-request-format)
                   :response-format (edn/edn-response-format)
                   :on-success [::upload-documentation-link-success]
                   :on-failure [::upload-documentation-link-failure]}})))

(rf/reg-event-fx
 ::documentation-link-upload-form-reset
 (fn documentation-link-upload-form-reset
   [_cofx [_]]
   (-> js/document
       (.getElementById "doc-link-upload-form")
       (.reset))
   {}))

(rf/reg-event-fx
 ::upload-documentation-link-success
 (fn upload-documentation-link-success
   [{:keys [db] :as _cofx} [_ documents]]
   {:db (-> db
            (db/set-link-uploading? false)
            (db/clear-documentation-link-upload-payload)
            (db/set-documents documents))
    :dispatch-n [[::documentation-link-upload-form-reset]
                 [::notification-handlers/add-alert :main
                  {:message "Task documentation link uploaded successfully"
                   :type :info}
                  :auto-dismiss? true]]}))

(rf/reg-event-fx
 ::upload-documentation-link-failure
 (fn upload-documentation-link-failure
   [{:keys [db] :as _cofx} [_ info]]
   {:db (db/set-link-uploading? db false)
    :dispatch [::notification-handlers/add-alert :main
               {:message (if-let [reason (get-in info [:response :reason])]
                           (str "Unable to upload task documentation link: " reason)
                           "Error uploading task documentation link")
                :type :error}
               :auto-dismiss? false]}))

;; Document Deletion

(rf/reg-event-fx
 ::delete-document
 (fn delete-document
   [_cofx [_ task-config-id document-id]]
   {:http-xhrio {:method :delete
                 :uri (kf/path-for [:api/task-document :task-config-id task-config-id :document-id document-id])
                 :format (edn/edn-request-format)
                 :response-format (edn/edn-response-format)
                 :on-success [::delete-document-success]
                 :on-failure [::delete-document-failure]}}))

(rf/reg-event-fx
 ::delete-document-success
 (fn delete-document-success
   [{:keys [db] :as _cofx} [_ documents]]
   {:db (db/set-documents db documents)
    :dispatch [::notification-handlers/add-alert :main
               {:message "Task document deleted successfully"
                :type :info}
               :auto-dismiss? true]}))

(rf/reg-event-fx
 ::delete-document-failure
 (fn delete-document-failure
   [_cofx [_ info]]
   {:dispatch [::notification-handlers/add-alert :main
               {:message (if-let [reason (get-in info [:response :reason])]
                           (str "Unable to delete task document: " reason)
                           "Error deleting task document")
                :type :error}
               :auto-dismiss? false]}))
